import { Component, effect, Inject, Input, OnChanges, OnDestroy, OnInit, PLATFORM_ID, SimpleChanges } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { MenuItem } from 'primeng/api';
import { AuthenticationFacade } from '../../../core/auth/authentication.facade';
import { CoreService } from '../../../services/core/core.service';
import { OrganizationSwitchService } from '../../../core/auth/organization-switch.service';
import { AppSettings } from '../../../services/settings/app-settings.service';
import { OrganizationContextService } from '../../../services/core/organization-context.service';

@Component({
  standalone: false,
  selector: 'app-user-actions',
  templateUrl: './user-actions.component.html',
  styleUrl: './user-actions.component.scss',
})
export class UserActionsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() moduleMenuItems: MenuItem[] = [];
  items: MenuItem[] | undefined = [];
  isMobile = false;
  drawerVisible = false;
  userName = 'Usuario';
  currentOrganizationName = 'Organización';
  userEmail = '';
  mobileItems: MenuItem[] = [];

  // Organizaciones del usuario para el submenú "Cambiar Organización"
  organizations: Array<{ id: string; name: string; active: boolean }> = [];
  currentOrgId: string | null = null;

  private resizeHandler = () => this.checkScreenSize();
  private orgNameRequestId = 0;

  constructor(
    private route: Router,
    private auth: AuthenticationFacade,
    private coreService: CoreService,
    private organizationSwitch: OrganizationSwitchService,
    private appSettings: AppSettings,
    private orgContext: OrganizationContextService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    effect(() => {
      const orgId = this.orgContext.getCurrentOrganization() || this.coreService.resolveCurrentOrganizationId();
      void this.syncCurrentOrganization(orgId);
    });
  }

  ngOnInit(): void {
    this.loadUserInfo();
    this.buildMenuItems();
    this.loadCurrentOrganizationName();
    this.loadUserOrganizations();

    if (isPlatformBrowser(this.platformId)) {
      this.checkScreenSize();
      window.addEventListener('resize', this.resizeHandler);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['moduleMenuItems']) {
      this.buildMenuItems();
    }
  }

  ngOnDestroy(): void {
    if (isPlatformBrowser(this.platformId)) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  onAvatarClick(event: Event, userActions: any): void {
    if (this.isMobile) {
      this.drawerVisible = true;
      return;
    }
    userActions.toggle(event);
  }

  onMobileItemClick(item: MenuItem): void {
    if (item?.disabled) {
      return;
    }
    if (item?.command) {
      item.command({ originalEvent: null as any, item } as any);
    }
    this.drawerVisible = false;
  }

  private buildMenuItems(): void {
    const moduleItems = (this.moduleMenuItems || []).map((item) => ({ ...item }));
    const hasModuleItems = moduleItems.length > 0;

    const profileItem: MenuItem = {
      label: 'Mi Perfil',
      icon: 'pi pi-user',
      command: () => {
        this.onProfileClick();
      },
    };

    const logoutItem: MenuItem = {
      label: 'Cerrar Sesión',
      icon: 'pi pi-sign-out',
      command: () => {
        this.onLogoutClick();
      },
    };

    // Submenú "Cambiar Organización" (solo si el usuario pertenece a más de una)
    const orgSwitchItem: MenuItem | null =
      this.organizations.length > 1
        ? {
            label: 'Cambiar Organización',
            icon: 'pi pi-building',
            items: this.organizations.map((org) => ({
              label: org.name,
              icon: org.id === this.currentOrgId ? 'pi pi-check' : 'pi pi-circle',
              disabled: org.id === this.currentOrgId || !org.active,
              command: () => this.onOrganizationChange(org.id),
            })),
          }
        : null;

    this.items = [
      ...moduleItems,
      ...(hasModuleItems ? [{ separator: true } as MenuItem] : []),
      profileItem,
      ...(orgSwitchItem || hasModuleItems ? [{ separator: true } as MenuItem] : []),
      ...(orgSwitchItem ? [orgSwitchItem, { separator: true } as MenuItem] : []),
      logoutItem,
    ];

    // En móvil (drawer plano) listamos las organizaciones como ítems directos.
    const mobileOrgItems: MenuItem[] =
      this.organizations.length > 1
        ? this.organizations.map((org) => ({
            label: org.name,
            icon: org.id === this.currentOrgId ? 'pi pi-check' : 'pi pi-building',
            disabled: org.id === this.currentOrgId || !org.active,
            command: () => this.onOrganizationChange(org.id),
          }))
        : [];

    this.mobileItems = [...moduleItems, profileItem, ...mobileOrgItems, logoutItem];
  }

  /** Carga las organizaciones del usuario para el submenú de cambio. */
  private async loadUserOrganizations(): Promise<void> {
    try {
      this.currentOrgId = this.coreService.resolveCurrentOrganizationId();
      const orgs =
        typeof this.orgContext.getMyOrganizationsWithStatus === 'function'
          ? await this.orgContext.getMyOrganizationsWithStatus(true)
          : (await this.coreService.listMyOrganizationsWithNames(true)).map((o) => ({
              id: o.id,
              name: o.name || o.id,
              active: true,
            }));
      this.organizations = (orgs || []).filter((o) => !!o?.id).map((o) => ({ id: o.id, name: o.name || o.id, active: o.active !== false }));
      this.buildMenuItems();
    } catch (error) {
      console.error('[UserActions] No se pudieron cargar las organizaciones del usuario:', error);
      this.organizations = [];
    }
  }

  /**
   * Cambia la organización activa: muestra el overlay global, re-emite la sesión scoped a la
   * org elegida (con su rol) y navega al home para recalcular módulos/servicios.
   */
  async onOrganizationChange(orgId: string): Promise<void> {
    const targetOrgId = String(orgId || '').trim();
    const currentOrgId = this.coreService.resolveCurrentOrganizationId();
    const latestOrgs = await this.refreshOrganizationsForSwitch();
    const targetOrg = latestOrgs.find((org) => org.id === targetOrgId);
    if (!targetOrgId || targetOrgId === currentOrgId || !targetOrg || targetOrg.active === false || this.appSettings.orgSwitching()) {
      return;
    }

    this.drawerVisible = false;
    this.appSettings.orgSwitching.set(true);
    try {
      await this.organizationSwitch.switchOrganization(targetOrgId);
      this.currentOrgId = targetOrgId;
      await this.loadCurrentOrganizationName();
      this.buildMenuItems();
      await this.route.navigate(['/home']);
    } catch (error) {
      console.error('[UserActions] Error al cambiar de organización:', error);
    } finally {
      this.appSettings.orgSwitching.set(false);
    }
  }

  private async refreshOrganizationsForSwitch(): Promise<Array<{ id: string; name: string; active: boolean }>> {
    try {
      const orgs =
        typeof this.orgContext.getMyOrganizationsWithStatus === 'function'
          ? await this.orgContext.getMyOrganizationsWithStatus(true)
          : (await this.coreService.listMyOrganizationsWithNames(true)).map((o) => ({
              id: o.id,
              name: o.name || o.id,
              active: true,
            }));
      this.organizations = (orgs || []).filter((o) => !!o?.id).map((o) => ({ id: o.id, name: o.name || o.id, active: o.active !== false }));
      this.buildMenuItems();
    } catch (error) {
      console.error('[UserActions] No se pudieron revalidar las organizaciones del usuario:', error);
    }
    return this.organizations;
  }

  private checkScreenSize(): void {
    this.isMobile = window.innerWidth <= 768;
  }

  private loadUserInfo(): void {
    const idToken = this.auth.getIdToken();
    const emailFromStorage = this.auth.getEmail() || '';

    if (!idToken) {
      this.userEmail = emailFromStorage;
      this.userName = emailFromStorage ? emailFromStorage.split('@')[0] : 'Usuario';
      return;
    }

    try {
      const payload = this.decodeToken(idToken);
      // Mostrar nombre + apellido (claims separados) tal como en mb-web-admin; si no están,
      // caer al nombre completo (name) y luego a otras alternativas.
      const given = (payload?.given_name || '').toString().trim();
      const family = (payload?.family_name || '').toString().trim();
      const composedName = [given, family].filter(Boolean).join(' ');
      this.userName = composedName || payload?.name || payload?.preferred_username || emailFromStorage.split('@')[0] || 'Usuario';
      this.userEmail = payload?.email || emailFromStorage;
    } catch {
      this.userEmail = emailFromStorage;
      this.userName = emailFromStorage ? emailFromStorage.split('@')[0] : 'Usuario';
    }
  }

  private async loadCurrentOrganizationName(): Promise<void> {
    await this.syncCurrentOrganization(this.coreService.resolveCurrentOrganizationId());
  }

  private async syncCurrentOrganization(orgId: string | null): Promise<void> {
    const requestId = ++this.orgNameRequestId;
    this.currentOrgId = orgId;
    this.buildMenuItems();

    if (!orgId || orgId === 'undefined') {
      this.currentOrganizationName = '';
      return;
    }

    try {
      const name = await this.coreService.resolveOrganizationName(orgId);
      if (requestId === this.orgNameRequestId) {
        this.currentOrganizationName = name;
      }
    } catch (error) {
      if (requestId === this.orgNameRequestId) {
        this.currentOrganizationName = 'Organización';
      }
      console.error('[UserActions] No se pudo resolver la organización actual:', error);
    }
  }

  private decodeToken(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Token inválido');
    }
    const decodedPayload = atob(parts[1]);
    return JSON.parse(decodedPayload);
  }

  onProfileClick() {
    this.drawerVisible = false;
    this.route.navigate(['profile']);
  }

  onSettingsClick() {
    console.log('Settings clicked');
    // Lógica para el clic en "Settings"
  }

  async onLogoutClick() {
    // Limpiar sesión local gestionada por backend
    this.auth.logoutSession();
    this.auth.clearApiSession();

    this.route.navigate(['login']);
  }

  onUserManagementClick() {
    this.route.navigate(['user-management']);
  }
}
